import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  MagnifyingGlassIcon as SearchIcon, 
  ArrowUpIcon as SortAscendingIcon, 
  ArrowDownIcon as SortDescendingIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { TableContext } from '../context/TableContext';
import { AuthContext } from '../context/AuthContext';

const GradePage = () => {
  const { user } = React.useContext(AuthContext);
  const { tables, selectedTable, setSelectedTable, fetchTables } = React.useContext(TableContext);
  const [grades, setGrades] = useState([]);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedQuiz, setSelectedQuiz] = useState('');
  const [courses, setCourses] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch tables if user has permission
        if (user && (user.role === 'admin' || user.role === 'teacher')) {
          fetchTables();
        }
        
        const [gradesRes, coursesRes, quizzesRes] = await Promise.all([
          api.get('/submissions'),
          api.get('/courses'),
          api.get('/quizzes')
        ]);
        
        setGrades(gradesRes.data);
        setCourses(coursesRes.data || []);
        setQuizzes(quizzesRes.data || []);
        setError(null);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }

      // Fetch table data if a table is selected
      if (selectedTable) {
        try {
          const tableData = await api.get(`/tables/${selectedTable.id}/data`);
          setGrades(tableData.data);
        } catch (error) {
          console.error('Error fetching table data:', error);
        }
      }
    };

    fetchAllData();
  }, [selectedTable]);
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredGrades = grades
    .filter(g => {
      const matchesSearch = g.User?.name.toLowerCase().includes(search.toLowerCase());
      const matchesCourse = !selectedCourse || g.Quiz?.Course?.id === selectedCourse;
      const matchesQuiz = !selectedQuiz || g.Quiz?.id === selectedQuiz;
      return matchesSearch && matchesCourse && matchesQuiz;
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      const aValue = a[sortField];
      const bValue = b[sortField];
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue) 
        : bValue.localeCompare(aValue);
    });

  const handleDelete = async (gradeId) => {
    if (window.confirm('Are you sure you want to delete this grade?')) {
      try {
        await api.delete(`/submissions/${gradeId}`);
        setGrades(prev => prev.filter(g => g.id !== gradeId));
      } catch (error) {
        console.error('Error deleting grade:', error);
      }
    }
  };

  const handleUpdate = async (grade) => {
    if (user.role === 'teacher') {
      try {
        const response = await api.put(`/submissions/${grade.id}`, {
          score: grade.score
        });
        setGrades(prev => prev.map(g => 
          g.id === grade.id ? response.data : g
        ));
      } catch (error) {
        console.error('Error updating grade:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-600 text-center">
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{selectedTable ? selectedTable.name : 'Grades Management'}</h2>
        <div className="flex gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <SearchIcon className="w-5 h-5" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          {user.role === 'teacher' && (
            <select
              value={selectedTable?.id || ''}
              onChange={e => setSelectedTable(tables.find(t => t.id === e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Table</option>
              {tables.map(table => (
                <option key={table.id} value={table.id}>{table.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="course-filter" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
              <select
                id="course-filter"
                value={selectedCourse}
                onChange={e => setSelectedCourse(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Courses</option>
                {Array.isArray(courses) ? courses.map(course => (
                  <option key={course.id} value={course.id}>{course.title}</option>
                )) : null}
              </select>
            </div>
            <div>
              <label htmlFor="quiz-filter" className="block text-sm font-medium text-gray-700 mb-1">Quiz</label>
              <select
                id="quiz-filter"
                value={selectedQuiz}
                onChange={e => setSelectedQuiz(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Quizzes</option>
                {Array.isArray(quizzes) ? quizzes.map(quiz => (
                  <option key={quiz.id} value={quiz.id}>{quiz.title}</option>
                )) : null}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search students..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('User.name')} className="flex items-center gap-1">
                    Student
                    {sortField === 'User.name' && (
                      sortDirection === 'asc' ? (
                        <SortAscendingIcon className="w-4 h-4" />
                      ) : (
                        <SortDescendingIcon className="w-4 h-4" />
                      )
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('Quiz.course.title')} className="flex items-center gap-1">
                    Course
                    {sortField === 'Quiz.course.title' && (
                      sortDirection === 'asc' ? (
                        <SortAscendingIcon className="w-4 h-4" />
                      ) : (
                        <SortDescendingIcon className="w-4 h-4" />
                      )
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('Quiz.title')} className="flex items-center gap-1">
                    Quiz
                    {sortField === 'Quiz.title' && (
                      sortDirection === 'asc' ? (
                        <SortAscendingIcon className="w-4 h-4" />
                      ) : (
                        <SortDescendingIcon className="w-4 h-4" />
                      )
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('score')} className="flex items-center gap-1">
                    Score
                    {sortField === 'score' && (
                      sortDirection === 'asc' ? (
                        <SortAscendingIcon className="w-4 h-4" />
                      ) : (
                        <SortDescendingIcon className="w-4 h-4" />
                      )
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredGrades.map((grade) => (
                <motion.tr
                  key={grade.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {grade.User?.name || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {grade.Quiz?.Course?.title || 'No Course'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {grade.Quiz?.title || 'No Quiz'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-lg font-semibold">{grade.score}</span>
                      <div className="ml-2 h-2 w-24 bg-gray-200 rounded-full">
                        <div 
                          className="h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${(grade.score / 100) * 100}%`,
                            backgroundColor: grade.score >= 70 ? 'green' : grade.score >= 50 ? 'orange' : 'red'
                          }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUpdate(grade)}
                        className="p-1.5 text-blue-500 hover:text-blue-600"
                        title="Edit Grade"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(grade.id)}
                        className="p-1.5 text-red-500 hover:text-red-600"
                        title="Delete Grade"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GradePage;
